import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeftRight, Plus, Loader2, Trash2 } from 'lucide-react';
import { useDateFormat } from '../../hooks/useDateFormat';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const authHeader = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
const fmt = (n) => Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 });

const STATUS_COLORS = {
  draft: 'bg-gray-100 text-gray-700 border-gray-300',
  pending_approval: 'bg-amber-100 text-amber-800 border-amber-300',
  approved: 'bg-emerald-100 text-emerald-800 border-emerald-300',
  rejected: 'bg-red-100 text-red-800 border-red-300',
  committed: 'bg-blue-100 text-blue-800 border-blue-300',
  reversed: 'bg-orange-100 text-orange-800 border-orange-300',
  cancelled: 'bg-gray-200 text-gray-700 border-gray-300',
};

export default function CommitmentTransferList() {
  const fmtDate = useDateFormat();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [deletingId, setDeletingId] = useState(null);

  const loadItems = async () => {
    setLoading(true);
    try {
      const url = statusFilter ? `${API_URL}/transfers?status=${statusFilter}` : `${API_URL}/transfers`;
      const { data } = await axios.get(url, authHeader());
      setItems(data.items || []);
    } catch { setItems([]); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadItems(); }, [statusFilter]);

  const handleDelete = async (e, t) => {
    e.stopPropagation();
    if (!window.confirm(`Delete draft transfer ${t.transfer_reference}? This cannot be undone.`)) return;
    setDeletingId(t.id);
    try {
      await axios.delete(`${API_URL}/transfers/${t.id}`, authHeader());
      setItems(prev => prev.filter(x => x.id !== t.id));
    } catch (err) {
      alert(err?.response?.data?.detail || 'Failed to delete transfer.');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6" data-testid="commitment-transfer-list">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900 inline-flex items-center gap-2">
          <ArrowLeftRight className="text-blue-600" size={22} /> Commitment Transfers
        </h1>
        <button onClick={() => navigate('/investors/transfers/new')} className="px-3 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 inline-flex items-center gap-1.5" data-testid="new-transfer-btn">
          <Plus size={14} /> New Transfer
        </button>
      </div>

      <div className="flex items-center gap-2">
        {['', 'draft', 'pending_approval', 'approved', 'committed', 'reversed', 'rejected'].map(s => (
          <button key={s || 'all'} onClick={() => setStatusFilter(s)}
                  className={`px-2.5 py-1 text-xs rounded-full border ${statusFilter === s ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700'}`}
                  data-testid={`status-filter-${s || 'all'}`}>
            {s ? s.replace('_', ' ') : 'All'}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="p-8 flex items-center justify-center text-gray-500"><Loader2 className="animate-spin mr-2" /> Loading…</div>
        ) : items.length === 0 ? (
          <div className="p-8 text-center text-gray-500 text-sm">
            No transfers yet. <button onClick={() => navigate('/investors/transfers/new')} className="text-blue-600 hover:underline">Raise the first one →</button>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr className="text-left text-xs text-gray-600 uppercase">
                <th className="px-3 py-2">Reference</th>
                <th className="px-3 py-2">Transferor → Transferee</th>
                <th className="px-3 py-2">Fund · Class</th>
                <th className="px-3 py-2 text-right">Commitment</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Reason</th>
                <th className="px-3 py-2">Created</th>
                <th className="px-3 py-2 w-10"></th>
              </tr>
            </thead>
            <tbody>
              {items.map(t => (
                <tr key={t.id} className="border-b hover:bg-gray-50 cursor-pointer"
                    onClick={() => navigate(`/investors/transfers/${t.id}`)}
                    data-testid={`transfer-row-${t.id}`}>
                  <td className="px-3 py-2 font-mono text-xs">{t.transfer_reference}</td>
                  <td className="px-3 py-2 text-xs">{t.transferor_account_name} → {t.transferee_account_name}</td>
                  <td className="px-3 py-2 text-xs">{t.fund_name} · {t.class_name}</td>
                  <td className="px-3 py-2 text-xs text-right">{fmt(t.commitment_amount_transferred)}</td>
                  <td className="px-3 py-2"><span className={`px-2 py-0.5 text-xs rounded-full border ${STATUS_COLORS[t.status]}`}>{t.status.replace('_', ' ')}</span></td>
                  <td className="px-3 py-2 text-xs">{t.reason}</td>
                  <td className="px-3 py-2 text-xs">{fmtDate(t.created_at?.slice(0, 10))}</td>
                  <td className="px-3 py-2">
                    {t.status === 'draft' && (
                      <button
                        onClick={(e) => handleDelete(e, t)}
                        disabled={deletingId === t.id}
                        className="p-1 rounded hover:bg-red-50 text-red-600 disabled:opacity-40"
                        title="Delete draft"
                        data-testid={`delete-draft-${t.id}`}
                      >
                        {deletingId === t.id ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
